/**
 * WorkWise naming convention (contract section 2).
 *
 *   app     <e>ww<nnn>app<NN>   pww047app01
 *   db      <e>ww<nnn>db<NN>    dww047db01
 *   gateway <e>mtwwt0<x>        pmtwwt01, dmtwwt03   (no customer number)
 *
 * <e> is "p" for production and "d" for dev and staging. <nnn> is the customer
 * number zero-padded to 3 digits. Names are auto-filled but editable; the
 * validators below reject a name whose letter does not match the environment or
 * whose digits do not match the customer number.
 *
 * Node-safe: relative imports only, no import.meta.
 */
import { ENVIRONMENT_OPTIONS, SLUG_MAX, workwiseKeyVault } from '../../config/workwise'
import type { WorkwiseEnvironment } from '../../types/workwise'
import { APP_VM_REGEX, CUSTOMER_NUMBER_REGEX, DB_VM_REGEX, GATEWAY_VM_REGEX } from './patterns'

function envOption(env: WorkwiseEnvironment) {
  return ENVIRONMENT_OPTIONS.find((o) => o.value === env) ?? ENVIRONMENT_OPTIONS[0]
}

/** "p" for production, "d" for dev and staging. */
export function envLetter(env: WorkwiseEnvironment): 'p' | 'd' {
  return envOption(env).letter
}

/** "dev" | "stg" | "prd" (TODO-confirm), used in database names and the DB connection reference. */
export function envToken(env: WorkwiseEnvironment): 'dev' | 'stg' | 'prd' {
  return envOption(env).token
}

// ─── Dedicated network names ─────────────────────────────────────────────────

/** Azure's VNet name limit; it decides how much of the customer name fits (RG 90 and subnet 80 are looser). */
export const NETWORK_NAME_MAX = 64

/**
 * The customer part of a Dedicated network name: letters and digits only, lower case
 * ("St Croix Foods, Inc." -> "stcroixfoodsinc"). Same rule as the backend.
 */
export function networkNameToken(customerName: string): string {
  return customerName.normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]/g, '')
}

// ─── Slug and customer number ────────────────────────────────────────────────

/** NFKD -> ascii, lower, runs of non [a-z0-9] -> "-", trim "-", max 40. */
export function slugify(input: string): string {
  const slug = input
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, SLUG_MAX)
    .replace(/-+$/g, '')
  return slug
}

/** A valid customer number is exactly three digits, 001 to 999. */
export function isValidCustomerNumber(value: string): boolean {
  return CUSTOMER_NUMBER_REGEX.test(value) && value !== '000'
}

/**
 * Zero-pads a 1 to 3 digit number ("47" or 47 -> "047"). Returns null when the
 * value is not an integer in 1..999.
 */
export function padCustomerNumber(value: string | number): string | null {
  const text = String(value).trim()
  if (!/^\d{1,3}$/.test(text)) return null
  const padded = text.padStart(3, '0')
  return isValidCustomerNumber(padded) ? padded : null
}

// ─── Name builders ───────────────────────────────────────────────────────────

function pad2(seq: number): string {
  return String(Math.trunc(seq)).padStart(2, '0')
}

/**
 * pww047app01. Returns "" while the customer number is not yet valid, so the
 * wizard can call it on every keystroke.
 */
export function appVmName(env: WorkwiseEnvironment, customerNumber: string | number, seq = 1): string {
  const nnn = padCustomerNumber(customerNumber)
  if (!nnn || seq < 0 || seq > 99) return ''
  return `${envLetter(env)}ww${nnn}app${pad2(seq)}`
}

/** dww047db01. Returns "" while the customer number is not yet valid. */
export function dbVmName(env: WorkwiseEnvironment, customerNumber: string | number, seq = 1): string {
  const nnn = padCustomerNumber(customerNumber)
  if (!nnn || seq < 0 || seq > 99) return ''
  return `${envLetter(env)}ww${nnn}db${pad2(seq)}`
}

/** pmtwwt01. Only x = 1..9 exists; returns "" otherwise. */
export function gatewayVmName(env: WorkwiseEnvironment, seq: number): string {
  if (!Number.isInteger(seq) || seq < 1 || seq > 9) return ''
  return `${envLetter(env)}mtwwt0${seq}`
}

/**
 * The Key Vault reference of the customer's DB connection string (shown
 * read-only in step 4): kv://kv-workwise-<env token>/ww-<nnn>-<env token>-db-connection
 * (TODO-confirm vault and secret naming). "" until the customer number is valid.
 */
export function dbConnectionRef(customerNumber: string | number, env: WorkwiseEnvironment): string {
  const nnn = padCustomerNumber(customerNumber)
  if (!nnn) return ''
  const token = envToken(env)
  return `kv://${workwiseKeyVault(token)}/ww-${nnn}-${token}-db-connection`
}

// ─── Validators (return an error message, or null when valid) ────────────────

function checkLetter(name: string, env: WorkwiseEnvironment): string | null {
  const letter = envLetter(env)
  if (name[0] === letter) return null
  const label = envOption(env).label
  return `Name must start with "${letter}" for ${label} (p = production, d = dev or staging).`
}

function checkDigits(name: string, customerNumber: string): string | null {
  const nnn = padCustomerNumber(customerNumber)
  if (!nnn) return null // the customer number field reports its own error
  return name.slice(3, 6) === nnn ? null : `Digits must match the customer number ${nnn}.`
}

export function validateAppVmName(
  name: string,
  env: WorkwiseEnvironment,
  customerNumber: string,
): string | null {
  if (!APP_VM_REGEX.test(name)) {
    return 'Use <e>ww<nnn>app<NN>, for example pww047app01.'
  }
  return checkLetter(name, env) ?? checkDigits(name, customerNumber)
}

export function validateDbVmName(
  name: string,
  env: WorkwiseEnvironment,
  customerNumber: string,
): string | null {
  if (!DB_VM_REGEX.test(name)) {
    return 'Use <e>ww<nnn>db<NN>, for example dww047db01.'
  }
  return checkLetter(name, env) ?? checkDigits(name, customerNumber)
}

export function validateGatewayVmName(name: string, env: WorkwiseEnvironment): string | null {
  if (!GATEWAY_VM_REGEX.test(name)) {
    return 'Use <e>mtwwt0<x> with x from 1 to 9, for example pmtwwt01.'
  }
  return checkLetter(name, env)
}
